/**
 * Per-instance semantic-segmentation mask storage.
 *
 * Each SOP instance may have ONE mask of size imageW × imageH. Each
 * pixel is a byte = class index from the class registry (0 means
 * "unlabelled"). A sidecar `classIdByIndex` Map tells us which class
 * each index corresponds to so we can remap on class-list changes.
 *
 * Persistence: masks are emitted as DICOM SEG objects on report-sign
 * (future work — for now kept in memory + JSON export).
 *
 * Render: a <canvas> is drawn alongside the DICOM renderer with CSS
 * transforms matching the viewport store. `composeMaskImage` fills a
 * given ImageData from the mask Uint8Array + palette.
 */
import { createSignal } from "solid-js";
import type { AnnotationClass } from "./classes";

export interface MaskLayer {
  width:  number;
  height: number;
  pixels: Uint8Array;                   // length = width*height
  classIdByIndex: Map<number, string>;  // index 0 = transparent
  nextIndex: number;                    // first free index (1..255)
}

const masks = new Map<string, MaskLayer>();

const [version, bump] = createSignal(0);
export { version as maskVersion };

export function getMask(uid: string): MaskLayer | undefined {
  return masks.get(uid);
}

export function ensureMask(uid: string, width: number, height: number): MaskLayer {
  let m = masks.get(uid);
  if (m && m.width === width && m.height === height) return m;
  m = {
    width, height,
    pixels: new Uint8Array(width * height),
    classIdByIndex: new Map(),
    nextIndex: 1,
  };
  masks.set(uid, m);
  bump(version() + 1);
  return m;
}

/** Paint a solid disc of the given class into the mask. */
export function brushDisc(
  uid:    string,
  cx:     number,
  cy:     number,
  radius: number,
  classId: string | null,  // null = erase (class index 0)
  cls:    AnnotationClass[],
): void {
  const m = masks.get(uid);
  if (!m) return;
  let idx = 0;
  if (classId) {
    // Assign / reuse an index for this class id
    for (const [i, cid] of m.classIdByIndex) if (cid === classId) { idx = i; break; }
    if (idx === 0) {
      idx = m.nextIndex++;
      m.classIdByIndex.set(idx, classId);
    }
  }
  const r2 = radius * radius;
  const x0 = Math.max(0, Math.floor(cx - radius));
  const x1 = Math.min(m.width  - 1, Math.ceil(cx + radius));
  const y0 = Math.max(0, Math.floor(cy - radius));
  const y1 = Math.min(m.height - 1, Math.ceil(cy + radius));
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      const dx = x - cx, dy = y - cy;
      if (dx * dx + dy * dy > r2) continue;
      m.pixels[y * m.width + x] = idx;
    }
  }
  bump(version() + 1);
}

/** Turn a mask into an RGBA ImageData using the supplied palette.
 *  Index 0 is always transparent. */
export function composeMaskImage(
  m:        MaskLayer,
  classes:  AnnotationClass[],
  opacity:  number,             // 0-1
): ImageData {
  const img = new ImageData(m.width, m.height);
  const palette = new Map<number, [number, number, number]>();
  for (const [idx, cid] of m.classIdByIndex) {
    const c = classes.find((cc) => cc.id === cid);
    if (!c) continue;
    palette.set(idx, hexToRgb(c.color));
  }
  const alpha = Math.max(0, Math.min(255, Math.round(opacity * 255)));
  for (let i = 0; i < m.pixels.length; i++) {
    const idx = m.pixels[i];
    if (idx === 0) { img.data[i * 4 + 3] = 0; continue; }
    const rgb = palette.get(idx);
    if (!rgb) { img.data[i * 4 + 3] = 0; continue; }
    img.data[i * 4]     = rgb[0];
    img.data[i * 4 + 1] = rgb[1];
    img.data[i * 4 + 2] = rgb[2];
    img.data[i * 4 + 3] = alpha;
  }
  return img;
}

export function clearAllMasks() {
  masks.clear();
  bump(version() + 1);
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3
    ? h.split("").map((c) => c + c).join("")
    : h, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
